import clsx from "clsx";
import React, { FC, useCallback, useEffect, useState } from "react";

import { getKeys } from "../../../utils/object";
import { Icon } from "../../icon";
import { useEventDetail } from "../event-detail/event-detail-context";
import { EventTypeTags } from "../event-type-tags";
import { EventsLabels } from "../types";
import { useEventDates } from "../use-event-dates";

export interface EventHeroCardInnerProps {
  className?: string;
}

interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const EventHeroCardInner: FC<EventHeroCardInnerProps> = ({ className }) => {
  const { event, labels } = useEventDetail();

  // This component is not rendered if event is null or undefined, we can safely use the non-null assertion
  const { formattedDateTimeRange, startDate } = useEventDates(event!);

  const getCountdown = useCallback(() => {
    // Get today's date and time
    const now = new Date().getTime();

    // Find the distance between now and the count down date
    const datesDiff = startDate.getTime() - now;

    // Time calculations for days, hours, minutes and seconds
    return datesDiff > 0
      ? ({
          days: Math.floor(datesDiff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((datesDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((datesDiff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((datesDiff % (1000 * 60)) / 1000),
        } satisfies Countdown)
      : null;
  }, [startDate]);

  const [countdown, setCountdown] = useState(getCountdown());

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(getCountdown());
    });

    return () => {
      clearInterval(interval);
    };
  }, [getCountdown]);

  return (
    <div className={clsx("rounded-2xl overflow-hidden", className)}>
      <div className="bg-primary-light p-2 flex flex-col gap-4">
        <EventTypeTags eventType={event!.eventType} labels={labels} />
        <div className="flex gap-2">
          <Icon icon="clock" />
          <div className="font-medium">{formattedDateTimeRange}</div>
        </div>
      </div>
      {countdown && (
        <div className="bg-primary-lightish text-center p-1">
          <div className="flex gap-3 justify-center">
            {getKeys(countdown).map((key, index) => (
              <div key={index}>
                <div>{String(countdown[key]).padStart(2, "0")}</div>
                <div className="text-xs">
                  {labels[("common-countdown-" + key) as keyof EventsLabels]?.toUpperCase()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
